import { useDispatch, useSelector } from 'react-redux';
import { addGuest, deleteGuest, addHost, deleteHost, addPresenter, deletePresenter, clearMeetingPanelParticipants,
    toggleDisableChat, toggleDisableReactions
} from '../../redux/Participants/actions';
import { createReactionAnimation, emojiMap } from '../Layout/ReactionsMenu';

const useMessageHandler = (currentUserId) => {
    const dispatch = useDispatch();
    const guests = useSelector(state => state.participants.guests);
    const hosts = useSelector(state => state.participants.hosts);
    const presenters = useSelector(state => state.participants.presenters);
    
    const checkIfUserExistsAlready = (userId) => {
        if (guests[userId] || hosts[userId] || presenters[userId]) {
            return true;
        }
        return false;
    };

    const handleJoinMessage = (message, setJoinTasksCompleted) => {
        if (!message.sender.startsWith("mp-") && !message.sender.startsWith("wt-")) {
            if (!checkIfUserExistsAlready(message.sender)) {
                try {
                    if (message.fromConvay && message.isHost) {
                        dispatch(addHost(message.sender, message.name));
                    } else if (message.fromConvay && !message.isHost) {
                        dispatch(addPresenter(message.sender, message.name));
                    } else if (!message.fromConvay && !message.isHost) {
                        dispatch(addGuest(message.sender, message.name));
                    }
                } catch (error) {
                    console.error("Error joining user:", error);
                }
            }
        }
        setJoinTasksCompleted(true);
    };

    const handleExistingUsersJoinMessage = (message, setJoinTasksCompleted) => {
        dispatch(clearMeetingPanelParticipants());

        message.users.forEach(user => {
            if (!user.sender.startsWith("mp-") && !user.sender.startsWith("wt-")) {
                try {
                    if (user.fromConvay && user.isHost) {
                        dispatch(addHost(user.sender, user.name));
                    } else if (user.fromConvay && !user.isHost) {
                        dispatch(addPresenter(user.sender, user.name));
                    } else if (!user.fromConvay && !user.isHost) {
                        dispatch(addGuest(user.sender, user.name));
                    }
                } catch (error) {
                    console.error("Error joining user:", error);
                }
            }
        });
        setJoinTasksCompleted(true);
    };

    const handleLeaveMessage = (message, isRecent, setRecent) => {
        if (isRecent) {
            setRecent(false);
        }
        let sender = message.sender;
        if (sender.startsWith("mp-") || sender.startsWith("wt-")) {
            sender = sender.replace("mp-", "").replace("wt-", "");
        }
        dispatch(deleteHost(sender));
        dispatch(deletePresenter(sender));
        dispatch(deleteGuest(sender));
    };

    const handleChatMessage = (message, setNewMessage, setNewMessageSender, setMessageToasterShow, setMessages, setNewMessageCounter) => {
        let processedContent = message.content;

        const convertEmojis = (text) => {
            return text.replace(/:([\w-]+):/g, (match, p1) => emojiMap[`:${p1}:`] || match);
        };

        // Check if the message content is an emoji (either direct emoji or emoji code)
        const isEmoji = Object.values(emojiMap).includes(processedContent) || 
                    Object.keys(emojiMap).includes(message.content);
        
        try {
            // Only try parsing if it's not already identified as an emoji
            if (!isEmoji) {
                const parsedContent = JSON.parse(message.content);
                processedContent = parsedContent;
            } else {
                processedContent = convertEmojis(message.content);
            }
        } catch (error) {
            processedContent = convertEmojis(message.content);
        }
        
        if (isEmoji && message.sender !== currentUserId) {
            createReactionAnimation(processedContent);
        }

        setMessages((prevMessages) => [...prevMessages, {
            ...message,
            content: processedContent,
            parentMessageId: message.parentMessageId,
            messageId: message.messageId,
            reactions: []
        }]);
        if (message.sender !== currentUserId) {
            setNewMessage(processedContent);
            setNewMessageSender(message.name);
            setMessageToasterShow(true);
            setNewMessageCounter(prevCounter => prevCounter + 1);
        }
    };

    const handleFileMessage = (message, setNewMessage, setNewMessageSender, setMessageToasterShow, setMessages, setNewMessageCounter) => {
        const fileContent = {
            type: 'FILE',
            fileName: message.content,
            downloadURL: message.downloadURL
        };

        setMessages((prevMessages) => [...prevMessages, {
            ...message,
            content: fileContent,
            messageId: message.messageId,
            parentMessageId: message.parentMessageId,
            reactions: []
        }]);

        if (message.sender !== currentUserId) {
            setNewMessage(`Shared a file: ${message.content}`);
            setNewMessageSender(message.name);
            setMessageToasterShow(true);
            setNewMessageCounter(prev => prev + 1);
        }
    }

    const handleChatReaction = (message, setMessages) => {
        try {
            const parsedContent = JSON.parse(message.content);
            setMessages(prevMessages => prevMessages.map(msg => {
                if (msg.messageId === parsedContent.messageId) {
                    const newReactions = Object.entries(parsedContent.reactions).flatMap(([reaction, users]) => 
                        users.map(user => ({
                            sender: user.userId,
                            name: user.userName,
                            reaction: reaction
                        }))
                    );
                    
                    return {
                        ...msg,
                        reactions: newReactions
                    };
                }
                return msg;
            }));
        } catch (error) {
            console.error('Error handling chat reaction:', error);
        }
    };

    const handleReactionMessage = (message, setNewMessage, setNewMessageSender, setMessageToasterShow, setMessages, setNewMessageCounter) => {
        try {
            const parsedContent = JSON.parse(message.content);
            const reaction = parsedContent.reaction;
            const messageId = parsedContent.messageId;
            
            const emojiReaction = emojiMap[reaction] || reaction;
            
            const displayContent = `${emojiReaction}`;
            
            const chatMessage = {
                messageId: message.messageId,
                content: displayContent,
                sender: message.sender,
                name: message.name,
                type: 'CHAT',
                timestamp: message.timestamp,
                parentMessageId: message.parentMessageId || null,
                reactions: [],
                fromConvay: message.fromConvay || false,
                isHost: message.isHost || false,
                isReaction: true,
                reactionToMessageId: messageId,
                originalReaction: emojiReaction
            };

            setMessages(prevMessages => [...prevMessages, chatMessage]);
            
            if (message.sender !== currentUserId) {
                setNewMessage(displayContent);
                setNewMessageSender(message.name);
                setMessageToasterShow(true);
                setNewMessageCounter(prevCounter => prevCounter + 1);
            }
        } catch (error) {
            console.error('Error handling reaction message:', error);
        }
    };

    const handleRaiseHandMessage = (message, hostsRef, presentersRef, setRaisedHands) => {
        const hostUser = Object.entries(hostsRef.current).find(([id, _]) => {
            const cleanId = id.replace(/^(mp-|wt-)/, '');
            return cleanId === message.sender;
        });

        const presenter = Object.entries(presentersRef.current).find(([id, _]) => {
            const cleanId = id.replace(/^(mp-|wt-)/, '');
            return cleanId === message.sender;
        });

        if (hostUser || presenter) {
            setRaisedHands(prev => new Set(prev).add(message.sender));
        }
    };

    const handleLowerHandMessage = (message, setRaisedHands) => {
        setRaisedHands(prev => {
            const updatedHands = new Set(prev);
            updatedHands.delete(message.sender);
            return updatedHands;
        });
    };

    const handleRaisedHandsListMessage = (message, joinTasksCompletedRef, hostsRef, presentersRef, setRaisedHands, onMessageReceived, payload) => {
        if (joinTasksCompletedRef.current === false) {
            setTimeout(() => onMessageReceived(payload), 100);
            return;
        }
        try {
            const raisedHandUsers = JSON.parse(message.content);
            setRaisedHands(prevRaisedHands => {
                const updatedRaisedHands = new Set(prevRaisedHands);
                raisedHandUsers.forEach(user => updatedRaisedHands.add(user));
                return updatedRaisedHands;
            });
        } catch (error) {
            console.error('Error parsing raised hands list:', error);
        }
    };

    /**
     * If a meeting panel user starts sharing their screen, add them to the screen presenters list.
     * @param {*} message => Message coming from big meeting backend
     * @param {*} setScreenPresenters => Function to set the screen presenters list
     */
    const handleStartScreenShare = (message, setScreenPresenters) => {
        setScreenPresenters(prev => new Set(prev).add(message.sender));
    }


    /**
     * If a meeting panel user stops sharing their screen, remove them from the screen presenters list.
     * @param {*} message => Message coming from big meeting backend
     * @param {*} setScreenPresenters => Function to set the screen presenters list
     */
    const handleStopScreenShare = (message, setScreenPresenters) => {
        setScreenPresenters(prev => {
            const updatedScreenPresenters = new Set(prev);
            updatedScreenPresenters.delete(message.sender);
            return updatedScreenPresenters;
        });
    }

    /**
     * If joined while a user is sharing their screen, big meeting backend sends a list of screen presenters before joining. 
     * Wait till all join tasks are completed before processing
     * @param {*} message => Message coming from big meeting backend
     * @param {*} setScreenPresenters => Function to set the screen presenters list
     */
    const handleScreenShareList = (message, joinTasksCompletedRef, setScreenPresenters, onMessageReceived, payload) => {
        if (joinTasksCompletedRef.current === false) {
            setTimeout(() => onMessageReceived(payload), 100);
            return;
        }
        const screenPresenterIds = message.content.split(',');
        screenPresenterIds.forEach(id => {
            setScreenPresenters(prev => new Set(prev).add(id));
        });
    }

    /**
     *  
     * @param {*} message => Message coming from big meeting backend
     */
    const handleInformAboutNewHost = (message) => {
        let sender = message.sender;
        if (sender.startsWith("mp-") || sender.startsWith("wt-")) {
            sender = sender.replace("mp-", "").replace("wt-", "");
        }
        dispatch(deletePresenter(sender));
        dispatch(addHost(sender, message.name));
    }

    const handleChatHistory = (message, setMessages) => {
        if (!Array.isArray(message.content)) {
            console.warn('Chat history content is not an array:', message.content);
            setMessages([]);
            return;
        }

        const chatHistory = message.content;
        const messageMap = new Map();
        
        try {
            chatHistory.forEach(item => {
                if (item.type === "CHAT") {
                    const chatMessage = {
                        messageId: item.messageId,
                        content: item.content,
                        sender: item.sender,
                        name: item.name,
                        timestamp: item.timestamp,
                        reactions: [],
                        parentMessageId: item.parentMessageId,
                        isChild: item.isChild
                    };
                    messageMap.set(item.messageId, chatMessage);
                }
            });
            
            chatHistory.forEach(item => {
                if (item.type === "CHAT_REACTION" && item.content.reactions) {
                    const messageId = item.content.messageId;
                    const message = messageMap.get(messageId);
                    if (message) {
                        message.reactions = item.content.reactions;
                    }
                }
            });
            
            // order all messages chronologically
            const processedMessages = Array.from(messageMap.values())
            .sort((a, b) => {
                const timeA = new Date(a.timestamp).getTime();
                const timeB = new Date(b.timestamp).getTime();
                return timeA - timeB;
            });
            
            setMessages(processedMessages);
        } catch (error) {
            console.error('Error processing chat history:', error);
            setMessages([]);
        }
    };


    const handleChatDisableMessage = (message) => {
        dispatch(toggleDisableChat(message.disableChatForGuests));
    }

    const handleReactionsDisableMessage = (message) => {
        dispatch(toggleDisableReactions(message?.disableReactionsForGuests ?? false));
    }

    const handleChatAndReactionConfig = (message) => {
        dispatch(toggleDisableChat(message.disableChatForGuests));
        dispatch(toggleDisableReactions(message?.disableReactionsForGuests ?? false));
    }

    return {
        handleJoinMessage,
        handleExistingUsersJoinMessage,
        handleLeaveMessage,
        handleChatMessage,
        handleFileMessage,
        handleReactionMessage,
        handleChatReaction,
        handleRaiseHandMessage,
        handleLowerHandMessage,
        handleRaisedHandsListMessage,
        handleStartScreenShare,
        handleStopScreenShare,
        handleScreenShareList,
        handleInformAboutNewHost,
        handleChatHistory,
        handleChatDisableMessage,
        handleReactionsDisableMessage,
        handleChatAndReactionConfig
    };
};

export default useMessageHandler;